import React, { useEffect, useState } from "react";

const API = (
  process.env.REACT_APP_SUPPLIER_ACK_API_URL || ""
).trim();

const styles = {
  page: {
    minHeight: "80vh",
    padding: "32px 18px",
    background: "#f3f6fa",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    fontFamily: "Arial, sans-serif"
  },
  card: {
    width: "100%",
    maxWidth: 650,
    background: "#fff",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 8px 30px rgba(20,43,67,.09)"
  },
  header: {
    background: "#17385c",
    padding: "28px 30px",
    color: "#fff"
  },
  body: {
    padding: "28px 30px",
    color: "#334155"
  },
  box: {
    background: "#f1f5f9",
    borderRadius: 10,
    padding: 18,
    marginBottom: 20
  },
  button: {
    width: "100%",
    padding: 16,
    border: 0,
    borderRadius: 10,
    background: "#176b49",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer"
  },
  label: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 7
  }
};

function readJwt(token) {
  try {
    const section = token.split(".")[1];
    const base64 = section
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    return JSON.parse(
      atob(base64.padEnd(
        Math.ceil(base64.length / 4) * 4,
        "="
      ))
    );
  } catch {
    return null;
  }
}

export default function SupplierAckPage() {
  const [ackToken] = useState(() => {
    const params = new URLSearchParams(
      window.location.search
    );
    return params.get("token") || "";
  });

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Reducir la exposicion del token en la URL.
  useEffect(() => {
    if (!ackToken) return;

    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname
    );
  }, [ackToken]);

  const accessToken =
    localStorage.getItem("accessToken") || "";

  const claims = readJwt(accessToken);

  const sessionValid = Boolean(
    claims &&
    claims.token_use === "access" &&
    claims.exp * 1000 > Date.now()
  );

  const isProvider = Boolean(
    claims &&
    (
      (Array.isArray(claims["cognito:groups"]) &&
        claims["cognito:groups"].some(
          group => ["admin", "provider"].includes(group)
        )) ||
      (typeof claims["cognito:groups"] === "string" &&
        ["admin", "provider"].some(
          group => claims["cognito:groups"]
            .replace(/[\[\]"]/g, "")
            .split(",")
            .map(value => value.trim())
            .includes(group)
        ))
    )
  );

  const apiReady = Boolean(
    /^https:\/\//i.test(API) &&
    !API.includes("example.com")
  );

  async function confirm() {
    if (!ackToken || !isProvider || !apiReady || busy) {
      return;
    }

    setBusy(true);
    setError("");

    try {
      const response = await fetch(`${API.replace(/\/$/, "")}/proveedor/acuse/confirmar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          token: ackToken
        })
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error ||
          "No fue posible registrar el acuse."
        );
      }

      setResult(data);
    } catch (err) {
      setError(
        err?.message ||
        "Error al registrar el acuse."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={styles.page}>
      <section style={styles.card}>
        <header style={styles.header}>
          <div style={{
            fontSize: 12,
            letterSpacing: 1.2,
            opacity: .8
          }}>
            IP DEPOT · CONTROL DE ABASTECIMIENTO
          </div>

          <h1 style={{
            margin: "12px 0 6px",
            fontSize: 25
          }}>
            Acuse de recibo
          </h1>

          <div style={{ opacity: .85, fontSize: 13 }}>
            Confirmación electrónica del proveedor
          </div>
        </header>

        <div style={styles.body}>
          {result ? (
            <>
              <div style={{
                ...styles.box,
                background: "#e8f5ee",
                color: "#176b49"
              }}>
                <strong>Recepción confirmada</strong>

                <p>
                  El acuse quedó registrado correctamente.
                </p>

                <div>
                  Folio: {result.folioSolicitud}
                </div>

                <div>
                  Fecha: {result.fechaConfirmacion}
                </div>

                {result.alreadyConfirmed && (
                  <p>
                    Esta solicitud ya había sido confirmada.
                  </p>
                )}
              </div>

              <p style={{ fontSize: 13 }}>
                Puedes continuar con tus actividades
                normales en el módulo de Remisiones.
              </p>
            </>
          ) : (
            <>
              <div style={styles.box}>
                <div style={styles.label}>
                  DOCUMENTO
                </div>

                <strong>
                  Solicitud de abastecimiento
                </strong>

                <p style={{
                  fontSize: 13,
                  lineHeight: 1.6,
                  marginBottom: 0
                }}>
                  Confirma exclusivamente que recibiste
                  la solicitud de materiales enviada
                  por IP DEPOT.
                </p>
              </div>

              <div style={styles.box}>
                <strong>
                  Alcance del acuse
                </strong>

                <p style={{
                  fontSize: 13,
                  lineHeight: 1.7,
                  marginBottom: 0
                }}>
                  Esta confirmación no registra envíos,
                  cantidades entregadas ni guías de
                  paquetería. Esas operaciones continúan
                  en el módulo de Remisiones.
                </p>
              </div>

              {!ackToken && (
                <p style={{ color: "#b45309" }}>
                  No se proporcionó un enlace de
                  confirmación válido.
                </p>
              )}

              {!sessionValid && (
                <p style={{ color: "#b45309" }}>
                  Inicia sesión con tu usuario Cognito
                  y vuelve a abrir el enlace original
                  del correo.
                </p>
              )}

              {sessionValid && !isProvider && (
                <p style={{ color: "#b91c1c" }}>
                  Tu usuario no tiene el rol de proveedor.
                </p>
              )}

              {!apiReady && (
                <p style={{ color: "#b45309" }}>
                  El servicio de confirmaciones todavía
                  no está habilitado.
                </p>
              )}

              {error && (
                <p role="alert" style={{
                  color: "#b91c1c",
                  fontSize: 13
                }}>
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={confirm}
                disabled={
                  !ackToken ||
                  !isProvider ||
                  !apiReady ||
                  busy
                }
                style={{
                  ...styles.button,
                  opacity: (
                    !ackToken ||
                    !isProvider ||
                    !apiReady ||
                    busy
                  ) ? .5 : 1,
                  cursor: busy ? "wait" : "pointer"
                }}
              >
                {busy
                  ? "Registrando acuse..."
                  : "CONFIRMAR RECEPCIÓN"}
              </button>

              <p style={{
                marginTop: 20,
                fontSize: 12,
                textAlign: "center",
                color: "#64748b"
              }}>
                La confirmación se registrará con
                fecha y hora del servidor.
              </p>
            </>
          )}
        </div>
      </section>
    </main>
  );
}